import { apiClient } from "@api/client";
import type { TokenPair } from "@api/types";
import { jwtDecode } from "jwt-decode";

type SessionScope = "clinic" | "user";

export interface ClinicSession {
  accessToken: string;
  refreshToken: string;
  accessExpiresAt: number;
  scope: SessionScope;
  clinicUuid: string;
  userUuid: string | null;
}

type ClinicAuthErrorCode =
  | "invalid_credentials"
  | "account_inactive"
  | "rate_limited"
  | "network_error"
  | "unknown";

export class ClinicAuthError extends Error {
  code: ClinicAuthErrorCode;
  status?: number;
  detail?: string;
  constructor(
    code: ClinicAuthErrorCode,
    message?: string,
    status?: number,
    detail?: string,
  ) {
    super(message ?? code);
    this.code = code;
    this.status = status;
    this.detail = detail;
    this.name = "ClinicAuthError";
  }
}

const fromStatus = (status: number): ClinicAuthError => {
  if (status === 401) return new ClinicAuthError("invalid_credentials");
  if (status === 429) return new ClinicAuthError("rate_limited");
  return new ClinicAuthError("unknown", `HTTP ${status}`);
};

const decodeJwtExp = (token: string): number => {
  try {
    const { exp } = jwtDecode<{ exp?: number }>(token);
    if (typeof exp !== "number") throw new Error("Missing exp claim");
    return exp * 1000;
  } catch {
    throw new ClinicAuthError("unknown", "Invalid token format");
  }
};

const isSessionScope = (scope: string): scope is SessionScope =>
  scope === "user" || scope === "clinic";

const toSession = (pair: TokenPair): ClinicSession => {
  if (!isSessionScope(pair.scope)) {
    throw new ClinicAuthError("unknown", `Unexpected scope: ${pair.scope}`);
  }
  return {
    accessToken: pair.access_token,
    refreshToken: pair.refresh_token,
    accessExpiresAt: decodeJwtExp(pair.access_token),
    scope: pair.scope,
    clinicUuid: pair.clinic_uuid,
    userUuid: pair.user_uuid ?? null,
  };
};

export const clinicAuthClient = {
  async login(subdomain: string, password: string): Promise<ClinicSession> {
    let result;
    try {
      result = await apiClient.POST("/auth/login", {
        body: { subdomain, password },
      });
    } catch {
      throw new ClinicAuthError("network_error");
    }
    if (!result.data) throw fromStatus(result.response.status);
    return toSession(result.data);
  },

  async refresh(refreshToken: string): Promise<ClinicSession> {
    let result;
    try {
      result = await apiClient.POST("/auth/refresh", {
        body: { refresh_token: refreshToken },
      });
    } catch {
      throw new ClinicAuthError("network_error");
    }
    if (!result.data) throw fromStatus(result.response.status);
    return toSession(result.data);
  },

  async selectUser(code: string, pin: string): Promise<ClinicSession> {
    let result;
    try {
      result = await apiClient.POST("/auth/select-user", {
        body: { code, pin },
      });
    } catch {
      throw new ClinicAuthError("network_error");
    }
    if (!result.data) {
      if (result.response.status === 403) {
        throw new ClinicAuthError("account_inactive", undefined, 403);
      }
      throw fromStatus(result.response.status);
    }
    return toSession(result.data);
  },

  async logout(refreshToken: string): Promise<void> {
    try {
      await apiClient.POST("/auth/logout", {
        body: { refresh_token: refreshToken },
      });
    } catch {
      // best-effort: client clears local state regardless
    }
  },
};
